

import HttpStatusCode from '../../httpClient/utils/HttpStatusCode';
import { SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { PartialPublishDialog } from '../components/PartialPublishDialog';
import { ISalesforceApp } from '../restApi/requests/GetSalesforceApps';
import { IParsedPublishToSalesforceResponse, IPublishToSalesforceResponse } from '../restApi/requests/PublishToSalesforce';
import { sendNavigateToSalesforceTelemetry, sendPublishCompletedTelemetry } from '../telemetry/PublishToSalesforceTelemetry';
import { PublishToSalesforceToaster } from './PublishToSalesforceToaster';

/**
 * Parses the PublishToSalesforceResponse and handles notifying the user of the publish outcome
 */
export class PublishToSalesforceResponseHandler {
  /**
   * Notifies the user of the publish outcome using toasts or the PartialPublishDialog
   * @param response publish to salesforce response
   * @param app app that views were published to
   */
  public static notifyUser(response: IPublishToSalesforceResponse, app: ISalesforceApp, views: IContentInfo[], sessionManagementService: SessionManagementService) {
    const parsedResponse: IParsedPublishToSalesforceResponse = this.parse(response, app.label, views, sessionManagementService);

    sendPublishCompletedTelemetry({
      num_successes: parsedResponse.numSuccesses,
      num_failures: parsedResponse.numFailures,
      view_luids: views.map((view) => view.luid).toString(),
      destination_app_id: app.id,
      session_management_service: sessionManagementService,
    });

    if (parsedResponse.numFailures === 0) {
      PublishToSalesforceToaster.popPublishSuccessToast(parsedResponse);
    } else if (parsedResponse.numSuccesses === 0) {
      PublishToSalesforceToaster.popPublishFailureToast(parsedResponse);
    } else {
      PartialPublishDialog.open(parsedResponse);
    }
  }

  private static parse(response: IPublishToSalesforceResponse, appName: string, views: IContentInfo[], sessionManagementService: SessionManagementService): IParsedPublishToSalesforceResponse {
    let parsedResponse: IParsedPublishToSalesforceResponse = {
      appName: `"${appName}"`,
      numSuccesses: 0,
      numFailures: 0,
      failureNames: [],
      successName: '',
      successAction: null
    };

    if (response.publishToSalesforce.salesforceAppUrl) {
      parsedResponse.successAction = this.getSuccessAction(response.publishToSalesforce.salesforceAppUrl, sessionManagementService);
    }

    if (!response.publishToSalesforce.hasErrors) {
      // If there are no errors, do not process publish info
      parsedResponse.numSuccesses = response.publishToSalesforce.publishToSalesforceInfo.length;
      parsedResponse.successName = `"${views[0].name}"`; // Default success name to first view
    } else {
      for (let publishInfo of response.publishToSalesforce.publishToSalesforceInfo) {
        if (publishInfo.statusCode === HttpStatusCode.Created.toString()) {
          parsedResponse.numSuccesses++;
          if (parsedResponse.numSuccesses === 1) {
            // If there is a single success, the toast will show the view name
            parsedResponse.successName = `"${views.find(view => view.luid === publishInfo.viewLuid).name}"`;
          }
        } else {
          parsedResponse.numFailures++;
          parsedResponse.failureNames.push(`"${views.find(view => view.luid === publishInfo.viewLuid).name}"`);
        }
      }
    }

    parsedResponse.failureNames.sort();
    return parsedResponse;
  }

  /**
   * Returns action to open link for successfully published views. Made public for testing purposes.
   */
  public static getSuccessAction(url: string, sessionManagementService: SessionManagementService) {
    return () => {
      window.open(url, '_blank');
      sendNavigateToSalesforceTelemetry(sessionManagementService.sessionSiteLuid(), sessionManagementService.sessionUserId());
    };
  }
}


